<template>
  <div class="p-4 text-center">
    <p v-if="!error">🔐 Signing you in… Please wait</p>
    <div v-else class="error-message">
      <p class="text-error">❌ Sign-in failed</p>
      <p class="text-body-2">{{ error }}</p>
      <v-btn color="primary" @click="retryLogin" class="mt-4">Retry Login</v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const auth = useAuthStore();
const error = ref(null);

const completeLoginFlow = async () => {
  let timeoutId;
  try {
    error.value = null;

    // Complete the login and get the original URL back
    const returnUrl = await Promise.race([
      auth.completeLogin(),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Sign-in timed out")), 10000);
      }),
    ]);

    // Fetch auth/me to check if system admin (must do before first navigation)
    const me = auth.authMe;
    const isSystemAdmin = me?.isSystemAdmin === true;

    // System admin always goes to admin view
    const safeReturnUrl = typeof returnUrl === "string" && returnUrl.startsWith("/") && !returnUrl.startsWith("//")
      ? returnUrl
      : "/";
    const targetPath = isSystemAdmin ? "/admin/customers" : safeReturnUrl;
    await router.replace(targetPath);
  } catch (err) {
    error.value = err.message || "Failed to complete sign-in. Please try again.";
    await auth.recoverToLogin("callback_failed", "/");
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const retryLogin = () => {
  error.value = null;
  auth.login("/");
};

onMounted(() => {
  completeLoginFlow();
});
</script>

<style scoped>
.error-message {
  padding: 2rem;
}

.text-error {
  color: #dc3545;
  font-weight: bold;
  margin-bottom: 1rem;
}
</style>
